import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Divider,
  Avatar,
  Card,
  CardContent,
  LinearProgress,
  useTheme,
} from "@mui/material";
import {
  Edit as EditIcon,
  Star as StarIcon,
  Archive as ArchiveIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  TrendingUp as TrendingUpIcon,
  Shield as ShieldIcon,
} from "@mui/icons-material";
import OverrideModal from "./RegisterOverrideModal";
import {
  getRiskColor,
  getRiskTextColor,
  validateOverride,
  getChecksEffectivenessForRisk,
} from "../../../../utils/riskUtils";
import { useQuery, useMutation } from "@apollo/client";
import {
  UPDATE_RISK,
  ARCHIVE_RISK,
} from "../../../../../../../apis/organization-apis/risk-apis/riskApis";
import { GET_STAFF_DETAILS } from "../../../../../../../apis/organization-apis/people-apis/peopleApis";
import ConfirmSnackbar from "../../../../../../../components/Snackbar/ConfirmSnackbar";
import { LevelSelectForm } from "../../../../../../../components/LevelSelectForm/LevelSelectForm";
import { EmptyFields, SourceTitle } from "../../../../../../../utils/enums";
import Role from "../../../../../../../utils/roles";
import { RiskScoreType } from "../../../../utils/enums";

export default function OverviewTab({ risk, refetchItems, onClose }) {
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [isEditingOwner, setIsEditingOwner] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState(risk?.risk_owner_email || "");

  const [updateRisk, { loading: updatingOwner }] = useMutation(UPDATE_RISK);
  const [archiveRisk, { loading: archiving }] = useMutation(ARCHIVE_RISK);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const { data: staffData, loading: staffLoading } = useQuery(GET_STAFF_DETAILS);

  const theme = useTheme();

  const adminEmails =
    staffData?.getEmployees?.data
      ?.filter((employee) =>
        employee?.roles?.some(
          (role) => role?.role_description === Role.ADMIN
        )
      )
      .map((employee) => employee?.emails?.[0]?.email)
      .filter(Boolean) || [];

  useEffect(() => {
    setOwnerEmail(risk?.risk_owner_email || "");
    setIsEditingOwner(false);
  }, [risk?.risk_owner_email, risk?.id]);

  const handleOverrideSave = async (values) => {
    const newScore = values?.inherent_impact * values?.inherent_likelihood;
    const validation = validateOverride(
      newScore,
      risk?.inherent_risk_score,
      RiskScoreType.INHERENT
    );
    if (!validation.isValid) {
      setSnackbarInfo({
        message: validation.error,
        vertical: "top",
        horizontal: "right",
      });
      console.error(validation.error);
      return;
    }
    setSnackbarInfo({
      message: "Updating risk...",
      vertical: "top",
      horizontal: "right",
    });
    try {
      const { data } = await updateRisk({
        variables: {
          risk_id: risk?.id,
          update_data: {
            inherent_impact: values?.inherent_impact,
            inherent_likelihood: values?.inherent_likelihood,
          },
        },
      });
      if (data?.updateRisk?.success) {
        setSnackbarInfo({
          message: "Risk updated successfully",
          vertical: "top",
          horizontal: "right",
        });
        refetchItems();
      } else {
        setSnackbarInfo({
          message: "Failed to update risk",
          vertical: "top",
          horizontal: "right",
        });
      }
      setOverrideModalOpen(false);
    } catch (err) {
      setSnackbarInfo({
        message: `Failed to update risk: ${err.message}`,
        vertical: "top",
        horizontal: "right",
      });
      console.error(err);
    }
  };

  const handleOwnerSave = async () => {
    if (!ownerEmail?.trim()) {
      setSnackbarInfo({
        message: "Risk owner email is required",
        vertical: "top",
        horizontal: "right",
      });
      return;
    }

    if (ownerEmail === risk?.risk_owner_email) {
      setIsEditingOwner(false);
      return;
    }

    try {
      const { data } = await updateRisk({
        variables: {
          risk_id: risk?.id,
          update_data: {
            risk_owner_email: ownerEmail.trim(),
          },
        },
      });

      if (data?.updateRisk?.success) {
        setSnackbarInfo({
          message: "Risk owner updated successfully",
          vertical: "top",
          horizontal: "right",
        });
        setIsEditingOwner(false);
        refetchItems();
      } else {
        setSnackbarInfo({
          message: data?.updateRisk?.message || "Failed to update risk owner",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (err) {
      setSnackbarInfo({
        message: `Error updating risk owner: ${err.message}`,
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const handleOwnerCancel = () => {
    setOwnerEmail(risk?.risk_owner_email || "");
    setIsEditingOwner(false);
  };

  const handleArchive = async () => {
    setSnackbarInfo({
      message: "Archiving risk...",
      vertical: "top",
      horizontal: "right",
    });
    try {
      const { data } = await archiveRisk({ variables: { id: risk?.id } });
      if (data?.deleteRisk?.success) {
        setSnackbarInfo({
          message: "Risk archived successfully",
          vertical: "top",
          horizontal: "right",
        });
        refetchItems();
        onClose();
      } else {
        setSnackbarInfo({
          message: "Failed to archive risk",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (err) {
      setSnackbarInfo({
        message: `Failed to archive risk: ${err.message}`,
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        height: "auto",
        backgroundColor: theme.customColors.background.dialog,
      }}
    >
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
      {overrideModalOpen && (
        <OverrideModal
          open={overrideModalOpen}
          onClose={() => setOverrideModalOpen(false)}
          onSave={handleOverrideSave}
          currentRisk={risk}
        />
      )}

      <Grid container spacing={2}>
        {/* Description Card */}
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
              transition: "box-shadow 0.3s ease",
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Typography
                variant="h5"
                fontWeight={600}
                sx={{ mb: 1.5, color: theme.customColors.text.strong }}
              >
                Description
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.6,
                  color: theme.customColors.text.strong,
                  backgroundColor: theme.customColors.background.header,
                  p: 1.5,
                  borderRadius: 1,
                  border: `1px solid ${theme.customColors.border.subtle}`,
                }}
              >
                {risk?.description || EmptyFields.DESCRIPTION}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Other Details Card */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              height: "100%",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
              transition: "box-shadow 0.3s ease",
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <InfoIcon
                  sx={{ color: theme.palette._blue.main, fontSize: 18 }}
                />
                <Typography
                  variant="h5"
                  fontWeight={600}
                  sx={{ color: theme.customColors.text.strong }}
                >
                  Other Details
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      backgroundColor: theme.customColors.background.tableRow,
                      color: theme.customColors.icons.edit,
                      width: 28,
                      height: 28,
                    }}
                  >
                    <CategoryIcon sx={{ fontSize: 14 }} />
                  </Avatar>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: "10px",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Category
                    </Typography>
                    <Chip
                      label={
                        risk?.risk_category_title || EmptyFields.RISK_CATEGORY
                      }
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        borderColor: theme.customColors.border.strong,
                        color: theme.customColors.text.strong,
                        mt: 0.5,
                        alignSelf: "flex-start",
                        height: "20px",
                      }}
                    />
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      backgroundColor: theme.customColors.background.tableRow,
                      color: theme.customColors.icons.edit,
                      width: 28,
                      height: 28,
                      mt: 0.25,
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 14 }} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: isEditingOwner ? 1 : 0,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: "10px",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Owner
                      </Typography>
                      {!isEditingOwner && (
                        <Tooltip title="Edit risk owner" arrow>
                          <IconButton
                            size="small"
                            onClick={() => setIsEditingOwner(true)}
                            sx={{
                              width: 20,
                              height: 20,
                              color: theme.customColors.text.secondary,
                              "&:hover": {
                                color: theme.customColors.text.strong,
                              },
                            }}
                            aria-label="Edit risk owner"
                          >
                            <EditIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    {isEditingOwner ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <LevelSelectForm
                          label="Risk Owner Email"
                          options={adminEmails.map((email) => ({
                            value: email,
                            label: email,
                          }))}
                          value={ownerEmail}
                          onChange={setOwnerEmail}
                          disabled={staffLoading || updatingOwner}
                        />
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleOwnerSave}
                            disabled={staffLoading || updatingOwner}
                          >
                            Save
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleOwnerCancel}
                            disabled={updatingOwner}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ color: theme.customColors.text.strong }}
                      >
                        {risk?.risk_owner_email || EmptyFields.OWNER}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      backgroundColor: theme.customColors.background.tableRow,
                      color: theme.customColors.icons.edit,
                      width: 28,
                      height: 28,
                    }}
                  >
                    <ShieldIcon sx={{ fontSize: 14 }} />
                  </Avatar>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: "10px",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Source
                    </Typography>
                    <Chip
                      label={risk?.risk_source_title || EmptyFields.SOURCE}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        ...(risk?.risk_source_title === SourceTitle.LIBRARY
                          ? {
                            borderColor: theme.palette._blue.main,
                            color: theme.palette._blue.main,
                            backgroundColor: theme.palette._blue.light,
                          }
                          : {
                            borderColor:
                              theme.customColors.status.success.main,
                            color: theme.customColors.status.success.main,
                            backgroundColor:
                              theme.customColors.status.success.bg,
                          }),
                        mt: 0.5,
                        alignSelf: "flex-start",
                        height: "20px",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Risk Assessment Card */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              height: "100%",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
              transition: "box-shadow 0.3s ease",
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <TrendingUpIcon
                  sx={{ color: theme.palette._blue.main, fontSize: 18 }}
                />
                <Typography
                  variant="h5"
                  fontWeight={600}
                  sx={{ color: theme.customColors.text.strong }}
                >
                  Risk Assessment
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {/* Inherent Risk */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: theme.customColors.background.dialog,
                      borderRadius: 2,
                      border: `1px solid ${theme.customColors.border.subtle}`,
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: theme.customColors.text.strong }}
                        >
                          Inherent Risk Score
                        </Typography>
                        <Tooltip
                          title="Default scores are placeholder values. Please override the score based on your business needs."
                          arrow
                        >
                          <StarIcon
                            sx={{
                              color: theme.customColors.status.warning.main,
                              fontSize: 12,
                            }}
                          />
                        </Tooltip>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => setOverrideModalOpen(true)}
                        sx={{
                          width: 24,
                          height: 24,
                          color: theme.customColors.icons.edit,
                          backgroundColor:
                            theme.customColors.background.tableRow,
                          "&:hover": {
                            backgroundColor: theme.customColors.border.subtle,
                            color: theme.customColors.text.strong,
                          },
                        }}
                        aria-label="Override inherent risk"
                      >
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 2,
                        backgroundColor: getRiskColor(
                          risk?.inherent_risk_score
                        ),
                        borderRadius: 2,
                        border: `2px solid ${getRiskTextColor(
                          risk?.inherent_risk_score
                        )}20`,
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="h2"
                        fontWeight={800}
                        sx={{
                          color: getRiskTextColor(risk?.inherent_risk_score),
                          textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        }}
                      >
                        {risk?.inherent_risk_score}
                      </Typography>
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: "center",
                        display: "block",
                        color: theme.customColors.text.muted,
                        fontWeight: 500,
                        fontSize: "10px",
                      }}
                    >
                      Impact: {risk?.inherent_impact} * Likelihood:{" "}
                      {risk?.inherent_likelihood}
                    </Typography>
                  </Box>
                </Grid>

                {/* Residual Risk */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: theme.customColors.background.dialog,
                      borderRadius: 2,
                      border: `1px solid ${theme.customColors.border.subtle}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          fontSize: "12px",
                          color: theme.customColors.text.strong,
                        }}
                      >
                        Residual Risk Score
                      </Typography>
                      <Tooltip title="Based on checks effectiveness" arrow>
                        <ShieldIcon
                          sx={{
                            color: theme.customColors.status.success.main,
                            fontSize: 12,
                          }}
                        />
                      </Tooltip>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 2,
                        backgroundColor: getRiskColor(
                          risk?.calculated_residual_risk_score
                        ),
                        borderRadius: 2,
                        border: `2px solid ${getRiskTextColor(
                          risk?.calculated_residual_risk_score
                        )}20`,
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="h2"
                        fontWeight={800}
                        sx={{
                          color: getRiskTextColor(
                            risk?.calculated_residual_risk_score
                          ),
                          textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        }}
                      >
                        {risk?.calculated_residual_risk_score}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.customColors.text.muted,
                            fontWeight: 500,
                            fontSize: "10px",
                          }}
                        >
                          Checks Effectiveness
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.customColors.text.strong,
                            fontWeight: 600,
                            fontSize: "10px",
                          }}
                        >
                          {getChecksEffectivenessForRisk(risk)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={getChecksEffectivenessForRisk(risk)}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: theme.customColors.border.subtle,
                          "& .MuiLinearProgress-bar": {
                            backgroundColor:
                              getChecksEffectivenessForRisk(risk) > 70
                                ? theme.customColors.status.success.main
                                : getChecksEffectivenessForRisk(risk) > 40
                                  ? theme.customColors.status.warning.main
                                  : theme.customColors.status.error.main,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
              alignItems: "center",
              p: 1.5,
              backgroundColor: "white",
              borderRadius: 2,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            {risk?.is_active === true && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.customColors.text.disabled,
                    fontStyle: "italic",
                    fontSize: "12px",
                  }}
                >
                  Archive this risk to remove it from active monitoring while
                  preserving its data.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<ArchiveIcon />}
                  onClick={handleArchive}
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    borderColor: theme.customColors.status.error.main,
                    color: theme.customColors.status.error.main,
                    borderWidth: "2px",
                    "&:hover": {
                      borderColor: theme.customColors.status.error.text,
                      backgroundColor: theme.customColors.status.error.bg,
                      color: theme.customColors.status.error.text,
                    },
                  }}
                  disabled={archiving}
                >
                  {archiving ? "Archiving..." : "Archive Risk"}
                </Button>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
